import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSalesReports, setFilters, resetFilters } from '../store/reportsSlice';
import { getServices } from '../store/serviceSlice';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { 
  FaChartBar, 
  FaFilter, 
  FaDownload, 
  FaArrowUp, 
  FaArrowDown,
  FaCalendarAlt,
  FaBuilding,
  FaCog,
  FaUsers,
  FaMoneyBillWave,
  FaPercentage,
  FaShoppingCart,
  FaCopy,
  FaFilePdf,
  FaImage,
  FaShare,
  FaFileExport
} from 'react-icons/fa';

const SalesReports = ({ userRole = 'Staff' }) => {
  const dispatch = useDispatch();
  const { reportsData, loading, error, filters } = useSelector((state) => state.reports);
  const { services } = useSelector((state) => state.services);
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState('thisMonth');
  const [isExporting, setIsExporting] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    dispatch(getServices());
    handleDateRangeChange('thisMonth');
  }, [dispatch]);

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    const today = new Date();
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    let startDate = '';
    let endDate = '';

    switch (range) {
      case 'today':
        startDate = today.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'yesterday':
        startDate = yesterday.toISOString().split('T')[0];
        endDate = yesterday.toISOString().split('T')[0];
        break;
      case 'thisWeek':
        // Monday to current day
        const currentDay = today.getDay();
        const mondayOffset = currentDay === 0 ? 6 : currentDay - 1; // Handle Sunday
        const weekStart = new Date(today.getTime() - (mondayOffset * 24 * 60 * 60 * 1000));
        startDate = weekStart.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'thisMonth':
        // 1st of current month to current date
        startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'lastMonth':
        // 1st to last date of previous month
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        startDate = lastMonth.toISOString().split('T')[0];
        endDate = lastMonthEnd.toISOString().split('T')[0];
        break;
      case 'last3Months':
        startDate = new Date(today.getFullYear(), today.getMonth() - 3, 1).toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'thisYear':
        startDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      default:
        break;
    }

    dispatch(setFilters({ startDate, endDate }));
    dispatch(getSalesReports({ ...filters, startDate, endDate }));
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    dispatch(setFilters(newFilters));
    dispatch(getSalesReports(newFilters));
  };

  const handleCustomDateFilter = () => {
    dispatch(getSalesReports(filters));
  };

  const formatCurrency = (amount) => {
    return (amount || 0).toLocaleString('en-IN');
  };

  // Helper function for CSV formatting (no commas to avoid cell splitting)
  const formatForCSV = (amount) => {
    return (amount || 0).toString();
  };

  // Helper function to get proper period display text
  const getPeriodText = () => {
    const today = new Date();
    
    if (filters.startDate && filters.endDate) {
      return `${filters.startDate} to ${filters.endDate}`;
    }
    
    // If no specific dates, show appropriate text based on common patterns
    const todayStr = today.toISOString().split('T')[0];
    const yesterdayStr = new Date(today.getTime() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    const thisWeekStart = new Date(today.getTime() - (today.getDay() * 24 * 60 * 60 * 1000)).toISOString().split('T')[0];
    const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().split('T')[0];
    const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0];
    
    if (filters.startDate === todayStr && filters.endDate === todayStr) {
      return 'Today';
    } else if (filters.startDate === yesterdayStr && filters.endDate === yesterdayStr) {
      return 'Yesterday';
    } else if (filters.startDate === thisMonthStart && filters.endDate === todayStr) {
      return 'This Month';
    } else if (filters.startDate === thisWeekStart && filters.endDate === todayStr) {
      return 'This Week';
    } else if (filters.startDate === lastMonthStart && filters.endDate === lastMonthEnd) {
      return 'Last Month';
    }
    
    return filters.startDate && filters.endDate ? `${filters.startDate} to ${filters.endDate}` : 'All Time';
  };

  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const getTrendIcon = (value) => {
    return value >= 0 ? (
      <FaArrowUp className="text-green-500" />
    ) : (
      <FaArrowDown className="text-red-500" />
    );
  };

  const getTrendColor = (value) => {
    return value >= 0 ? 'text-green-600' : 'text-red-600';
  };

  // Enhanced Export Functions
  const exportToCSV = async () => {
    if (!reportsData) return;
    
    // Get detailed sales data with filters
    const detailedReports = await dispatch(getSalesReports({ ...filters, reportType: 'detailed' })).unwrap();
    
    // Create comprehensive CSV data
    const csvData = [];
    
    // Add summary data
    csvData.push(['SALES REPORT SUMMARY']);
    csvData.push(['Period', getPeriodText()]);
    csvData.push(['Total Sales', formatForCSV(reportsData.summary?.totalSales || 0)]);
    csvData.push(['Total Profit', formatForCSV(reportsData.summary?.totalProfit || 0)]);
    csvData.push(['Total Transactions', reportsData.summary?.totalTransactions || 0]);
    csvData.push(['Profit Margin', `${(reportsData.summary?.profitMargin || 0).toFixed(1)}%`]);
    csvData.push([]);
    
    // Add branch performance
    if (reportsData.breakdown?.byBranch?.length > 0) {
      csvData.push(['BRANCH PERFORMANCE']);
      csvData.push(['Branch', 'Sales', 'Profit', 'Transactions', 'Avg Order']);
      reportsData.breakdown.byBranch.forEach(branch => {
        csvData.push([
          branch._id || 'N/A',
          formatForCSV(branch.totalSales || 0),
          formatForCSV(branch.totalProfit || 0),
          branch.transactionCount || 0,
          formatForCSV(branch.avgOrderValue || 0)
        ]);
      });
      csvData.push([]);
    }
    
    // Add service performance
    if (reportsData.breakdown?.byService?.length > 0) {
      csvData.push(['TOP SERVICES']);
      csvData.push(['Service', 'Sales', 'Profit', 'Orders', 'Avg Order']);
      reportsData.breakdown.byService.forEach(service => {
        csvData.push([
          service.serviceName || 'N/A',
          formatForCSV(service.totalSales || 0),
          formatForCSV(service.totalProfit || 0),
          service.transactionCount || 0,
          formatForCSV(service.avgOrderValue || 0)
        ]);
      });
      csvData.push([]);
    }
    
    // Add staff performance (Admin only)
    if (userRole === 'Admin' && reportsData.breakdown?.byStaff?.length > 0) {
      csvData.push(['STAFF PERFORMANCE']);
      csvData.push(['Staff Member', 'Email', 'Sales', 'Profit', 'Orders', 'Avg Order']);
      reportsData.breakdown.byStaff.forEach(staff => {
        csvData.push([
          staff.staffName || 'N/A',
          staff.staffEmail || 'N/A',
          formatForCSV(staff.totalSales || 0),
          formatForCSV(staff.totalProfit || 0),
          staff.transactionCount || 0,
          formatForCSV(staff.avgOrderValue || 0)
        ]);
      });
      csvData.push([]);
    }
    
    // Add detailed sales records (filtered)
    if (detailedReports.salesData && detailedReports.salesData.length > 0) {
      csvData.push(['DETAILED SALES RECORDS (FILTERED)']);
      csvData.push(['Sr.No', 'Date', 'Time', 'Service', 'Staff Member', 'Sale Amount', 'Profit']);
      
      detailedReports.salesData.forEach((sale, index) => {
        const saleDate = new Date(sale.createdAt);
        
        csvData.push([
          index + 1,
          saleDate.toLocaleDateString('en-IN'),
          saleDate.toLocaleTimeString('en-IN', { hour12: true }),
          sale.service?.name || 'N/A',
          sale.user?.name || 'N/A',
          formatForCSV(sale.price || 0),
          formatForCSV(sale.profit || 0)
        ]);
      });
    }

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `shahji-sales-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Copy as High-Quality Image
  const copyAsImage = async () => {
    if (!reportRef.current) return;
    
    setIsExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2, // High quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#f9fafb',
        width: reportRef.current.scrollWidth,
        height: reportRef.current.scrollHeight,
        scrollX: 0,
        scrollY: 0
      });
      
      // Convert to blob and copy to clipboard
      canvas.toBlob(async (blob) => {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': blob })
          ]);
          alert('Report copied to clipboard as image! You can now paste it in WhatsApp or any other app.');
        } catch (err) {
          // Fallback: Download the image
          const url = canvas.toDataURL('image/png');
          const a = document.createElement('a');
          a.href = url;
          a.download = `shahji-sales-report-${new Date().toISOString().split('T')[0]}.png`;
          a.click();
          alert('Image downloaded! You can share this file.');
        }
      }, 'image/png', 1.0);
    } catch (error) {
      console.error('Error copying image:', error);
      alert('Error copying image. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Export as PDF
  const exportAsPDF = async () => {
    if (!reportRef.current) return;
    
    setIsExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: reportRef.current.scrollWidth,
        height: reportRef.current.scrollHeight
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      pdf.save(`shahji-sales-report-${new Date().toISOString().split('T')[0]}.pdf`);
      alert('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Copy PDF to clipboard (as file)
  const copyAsPDF = async () => {
    if (!reportRef.current) return;
    
    setIsExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#f9fafb'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      const pdfBlob = pdf.output('blob');
      
      try {
        await navigator.clipboard.write([
          new ClipboardItem({ 'application/pdf': pdfBlob })
        ]);
        alert('Report PDF copied to clipboard!');
      } catch (err) {
        console.log('Clipboard copy failed, downloading instead:', err);
        pdf.save(`shahji-sales-report-${new Date().toISOString().split('T')[0]}.pdf`);
        alert('PDF downloaded (clipboard copy not supported in this browser)');
      }
    } catch (error) {
      console.error('PDF copy error:', error);
      alert('Error copying PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Export Data as PDF (like CSV but in PDF format)
  const exportDataAsPDF = async () => {
    if (!reportsData) return;
    setIsExporting(true);
    
    try {
      // Get detailed sales data with filters
      const detailedReports = await dispatch(getSalesReports({ ...filters, reportType: 'detailed' })).unwrap();
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      let yPosition = 20;
      const pageHeight = 280;
      const lineHeight = 6;
      
      // Helper function to add new page if needed
      const checkPageBreak = (requiredSpace = 10) => {
        if (yPosition + requiredSpace > pageHeight) {
          pdf.addPage();
          yPosition = 20;
        }
      };
      
      // Title
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('SHAHJI COMPUTERS - SALES REPORT', 105, yPosition, { align: 'center' });
      yPosition += 10;
      
      // Date line
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Generated on: ${new Date().toLocaleDateString('en-IN')} at ${new Date().toLocaleTimeString('en-IN')}`, 105, yPosition, { align: 'center' });
      yPosition += 15;
      
      // Summary Section with box
      pdf.setDrawColor(0, 0, 0);
      pdf.rect(15, yPosition - 5, 180, 45);
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('SUMMARY', 20, yPosition);
      yPosition += 10;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Period: ${getPeriodText()}`, 20, yPosition);
      yPosition += 6;
      
      // Two column layout for summary
      pdf.text(`Total Sales: ${formatCurrency(reportsData.summary?.totalSales || 0)}`, 20, yPosition);
      pdf.text(`Total Profit: ${formatCurrency(reportsData.summary?.totalProfit || 0)}`, 110, yPosition);
      yPosition += 6;
      pdf.text(`Total Transactions: ${reportsData.summary?.totalTransactions || 0}`, 20, yPosition);
      pdf.text(`Profit Margin: ${(reportsData.summary?.profitMargin || 0).toFixed(1)}%`, 110, yPosition);
      yPosition += 20;
      
      // Branch Performance
      if (reportsData.breakdown?.byBranch?.length > 0) {
        checkPageBreak(40);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('BRANCH PERFORMANCE', 20, yPosition);
        yPosition += 10;
        
        // Table header
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Branch', 20, yPosition);
        pdf.text('Sales', 70, yPosition);
        pdf.text('Profit', 110, yPosition);
        pdf.text('Orders', 150, yPosition);
        pdf.text('Avg Order', 175, yPosition);
        yPosition += 2;
        
        // Draw line under header
        pdf.line(20, yPosition, 200, yPosition);
        yPosition += 4;
        
        pdf.setFont('helvetica', 'normal');
        reportsData.breakdown.byBranch.forEach((branch, index) => {
          checkPageBreak();
          pdf.text(`${branch._id || 'N/A'}`, 20, yPosition);
          pdf.text(`${formatCurrency(branch.totalSales || 0)}`, 70, yPosition);
          pdf.text(`${formatCurrency(branch.totalProfit || 0)}`, 110, yPosition);
          pdf.text(`${branch.transactionCount || 0}`, 150, yPosition);
          pdf.text(`${formatCurrency(branch.avgOrderValue || 0)}`, 175, yPosition);
          yPosition += 5;
        });
        yPosition += 10;
      }
      
      // Service Performance
      if (reportsData.breakdown?.byService?.length > 0) {
        checkPageBreak(40);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('TOP SERVICES', 20, yPosition);
        yPosition += 10;
        
        // Table header
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Service', 20, yPosition);
        pdf.text('Sales', 90, yPosition);
        pdf.text('Profit', 130, yPosition);
        pdf.text('Orders', 170, yPosition);
        yPosition += 2;
        
        // Draw line under header
        pdf.line(20, yPosition, 200, yPosition);
        yPosition += 4;
        
        pdf.setFont('helvetica', 'normal');
        reportsData.breakdown.byService.forEach((service, index) => {
          checkPageBreak();
          const serviceName = service.serviceName || 'N/A';
          // Truncate long service names
          const displayName = serviceName.length > 25 ? serviceName.substring(0, 25) + '...' : serviceName;
          pdf.text(displayName, 20, yPosition);
          pdf.text(`${formatCurrency(service.totalSales || 0)}`, 90, yPosition);
          pdf.text(`${formatCurrency(service.totalProfit || 0)}`, 130, yPosition);
          pdf.text(`${service.transactionCount || 0}`, 170, yPosition);
          yPosition += 5;
        });
        yPosition += 10;
      }
      
      // Staff Performance (Admin only)
      if (userRole === 'Admin' && reportsData.breakdown?.byStaff?.length > 0) {
        checkPageBreak(40);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('STAFF PERFORMANCE', 20, yPosition);
        yPosition += 10;
        
        // Table header
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Staff Member', 20, yPosition);
        pdf.text('Sales', 80, yPosition);
        pdf.text('Profit', 120, yPosition);
        pdf.text('Orders', 160, yPosition);
        yPosition += 2;
        
        // Draw line under header
        pdf.line(20, yPosition, 200, yPosition);
        yPosition += 4;
        
        pdf.setFont('helvetica', 'normal');
        reportsData.breakdown.byStaff.forEach((staff, index) => {
          checkPageBreak();
          const staffName = staff.staffName || 'N/A';
          // Truncate long staff names
          const displayName = staffName.length > 20 ? staffName.substring(0, 20) + '...' : staffName;
          pdf.text(displayName, 20, yPosition);
          pdf.text(`${formatCurrency(staff.totalSales || 0)}`, 80, yPosition);
          pdf.text(`${formatCurrency(staff.totalProfit || 0)}`, 120, yPosition);
          pdf.text(`${staff.transactionCount || 0}`, 160, yPosition);
          yPosition += 5;
        });
        yPosition += 10;
      }
      
      // Detailed Sales Records
      if (detailedReports.salesData && detailedReports.salesData.length > 0) {
        checkPageBreak(50);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('DETAILED SALES RECORDS (FILTERED)', 20, yPosition);
        yPosition += 10;
        
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Sr.', 20, yPosition);
        pdf.text('Date', 30, yPosition);
        pdf.text('Time', 55, yPosition);
        pdf.text('Service', 80, yPosition);
        pdf.text('Staff', 130, yPosition);
        pdf.text('Sale Amount', 160, yPosition);
        pdf.text('Profit', 185, yPosition);
        yPosition += 2;
        
        // Draw line under header
        pdf.line(20, yPosition, 200, yPosition);
        yPosition += 4;
        
        pdf.setFont('helvetica', 'normal');
        
        detailedReports.salesData.forEach((sale, index) => {
          checkPageBreak(6);
          const saleDate = new Date(sale.createdAt);
          
          pdf.text(`${index + 1}`, 20, yPosition);
          pdf.text(`${saleDate.toLocaleDateString('en-IN')}`, 30, yPosition);
          pdf.text(`${saleDate.toLocaleTimeString('en-IN', { hour12: true })}`, 55, yPosition);
          
          const serviceName = sale.service?.name || 'N/A';
          const displayService = serviceName.length > 20 ? serviceName.substring(0, 20) + '...' : serviceName;
          pdf.text(displayService, 80, yPosition);
          
          const staffName = sale.user?.name || 'N/A';
          const displayStaff = staffName.length > 12 ? staffName.substring(0, 12) + '...' : staffName;
          pdf.text(displayStaff, 130, yPosition);
          
          pdf.text(`${formatCurrency(sale.price || 0)}`, 160, yPosition);
          pdf.text(`${formatCurrency(sale.profit || 0)}`, 185, yPosition);
          
          yPosition += 5;
          
          // Add separator line every 10 records
          if ((index + 1) % 10 === 0 && index < detailedReports.salesData.length - 1) {
            pdf.setDrawColor(200, 200, 200);
            pdf.line(20, yPosition, 200, yPosition);
            pdf.setDrawColor(0, 0, 0);
            yPosition += 2;
          }
        });
      }
      
      pdf.save(`shahji-sales-data-report-${new Date().toISOString().split('T')[0]}.pdf`);
      alert('Detailed sales data exported as PDF!');
    } catch (error) {
      console.error('Data PDF export error:', error);
      alert('Error exporting data as PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-24 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="text-center py-8">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Reports</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => dispatch(getSalesReports(filters))}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" ref={reportRef}>
      {/* Header with Filters */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-700 text-white p-6 rounded-2xl shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold mb-2">
              <FaChartBar className="inline mr-2" />
              Sales Reports & Analytics
            </h1>
            <p className="text-blue-100">
              {userRole === 'Admin' ? 'Company-wide' : 'Personal'} sales performance insights
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 backdrop-blur-sm border border-white border-opacity-30"
            >
              <FaFilter className="inline mr-2" />
              Filters
            </button>
            
            {/* Export Dropdown */}
            <div className="relative group">
              <button
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
                disabled={!reportsData || isExporting}
              >
                {isExporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <FaShare className="inline mr-2" />
                    Export & Share
                  </>
                )}
              </button>
              
              {/* Dropdown Menu */}
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <button
                    onClick={copyAsImage}
                    disabled={!reportsData || isExporting}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaImage className="mr-3 text-blue-500" />
                    <div>
                      <div className="font-medium">Copy as Image</div>
                      <div className="text-xs text-gray-500">High quality for WhatsApp</div>
                    </div>
                  </button>
                  
                  <button
                    onClick={exportAsPDF}
                    disabled={!reportsData || isExporting}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-red-50 hover:text-red-600 flex items-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaFilePdf className="mr-3 text-red-500" />
                    <div>
                      <div className="font-medium">Download PDF</div>
                      <div className="text-xs text-gray-500">Professional format</div>
                    </div>
                  </button>
                  
                  <button
                    onClick={copyAsPDF}
                    disabled={!reportsData || isExporting}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-purple-50 hover:text-purple-600 flex items-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaCopy className="mr-3 text-purple-500" />
                    <div>
                      <div className="font-medium">Copy PDF</div>
                      <div className="text-xs text-gray-500">Copy to clipboard</div>
                    </div>
                  </button>
                  
                  <hr className="my-2" />
                  
                  <button
                    onClick={exportDataAsPDF}
                    disabled={!reportsData || isExporting}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-600 flex items-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaFileExport className="mr-3 text-orange-500" />
                    <div>
                      <div className="font-medium">Export Data as PDF</div>
                      <div className="text-xs text-gray-500">Detailed records like CSV</div>
                    </div>
                  </button>
                  
                  <hr className="my-2" />
                  
                  <button
                    onClick={exportToCSV}
                    disabled={!reportsData || isExporting}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-green-50 hover:text-green-600 flex items-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaDownload className="mr-3 text-green-500" />
                    <div>
                      <div className="font-medium">Export CSV</div>
                      <div className="text-xs text-gray-500">Data for Excel</div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Date Range Filters */}
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'today', label: 'Today' },
            { key: 'thisWeek', label: 'This Week' },
            { key: 'thisMonth', label: 'This Month' },
            { key: 'lastMonth', label: 'Last Month' },
            { key: 'last3Months', label: 'Last 3 Months' },
            { key: 'thisYear', label: 'This Year' }
          ].map(range => (
            <button
              key={range.key}
              onClick={() => handleDateRangeChange(range.key)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                dateRange === range.key
                  ? 'bg-white text-blue-600'
                  : 'bg-white bg-opacity-20 hover:bg-opacity-30 text-white'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            <FaCog className="inline mr-2" />
            Advanced Filters
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Branch</label>
              <select
                value={filters.branch}
                onChange={(e) => handleFilterChange('branch', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Branches</option>
                <option value="Katra">Katra</option>
                <option value="Meeratola">Meeratola</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Service</label>
              <select
                value={filters.serviceId}
                onChange={(e) => handleFilterChange('serviceId', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Services</option>
                {services?.map(service => (
                  <option key={service._id} value={service._id}>
                    {service.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleCustomDateFilter}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Summary Statistics */}
      {reportsData?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Sales</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(reportsData.summary.totalSales)}
                </p>
                {reportsData.growth && (
                  <div className={`flex items-center mt-1 ${getTrendColor(reportsData.growth.salesGrowth)}`}>
                    {getTrendIcon(reportsData.growth.salesGrowth)}
                    <span className="ml-1 text-sm font-medium">
                      {formatPercentage(reportsData.growth.salesGrowth)}
                    </span>
                  </div>
                )}
              </div>
              <FaMoneyBillWave className="text-3xl text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Profit</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(reportsData.summary.totalProfit)}
                </p>
                {reportsData.growth && (
                  <div className={`flex items-center mt-1 ${getTrendColor(reportsData.growth.profitGrowth)}`}>
                    {getTrendIcon(reportsData.growth.profitGrowth)}
                    <span className="ml-1 text-sm font-medium">
                      {formatPercentage(reportsData.growth.profitGrowth)}
                    </span>
                  </div>
                )}
              </div>
              <FaArrowUp className="text-3xl text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Transactions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {reportsData.summary.totalTransactions}
                </p>
                {reportsData.growth && (
                  <div className={`flex items-center mt-1 ${getTrendColor(reportsData.growth.transactionGrowth)}`}>
                    {getTrendIcon(reportsData.growth.transactionGrowth)}
                    <span className="ml-1 text-sm font-medium">
                      {formatPercentage(reportsData.growth.transactionGrowth)}
                    </span>
                  </div>
                )}
              </div>
              <FaShoppingCart className="text-3xl text-purple-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Profit Margin</p>
                <p className="text-2xl font-bold text-gray-900">
                  {reportsData.summary.profitMargin.toFixed(1)}%
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Avg: {formatCurrency(reportsData.summary.averageOrderValue)}
                </p>
              </div>
              <FaPercentage className="text-3xl text-orange-500" />
            </div>
          </div>
        </div>
      )}

      {/* Branch Performance */}
      {reportsData?.breakdown?.byBranch && reportsData.breakdown.byBranch.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            <FaBuilding className="inline mr-2" />
            Branch Performance
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reportsData.breakdown.byBranch.map((branch, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">{branch._id}</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sales:</span>
                    <span className="font-medium">{formatCurrency(branch.totalSales)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Profit:</span>
                    <span className="font-medium text-green-600">{formatCurrency(branch.totalProfit)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Transactions:</span>
                    <span className="font-medium">{branch.transactionCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Avg Order:</span>
                    <span className="font-medium">{formatCurrency(branch.avgOrderValue)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Service Performance */}
      {reportsData?.breakdown?.byService && reportsData.breakdown.byService.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            <FaCog className="inline mr-2" />
            Top Services
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Service</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Sales</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Profit</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Orders</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Avg Order</th>
                </tr>
              </thead>
              <tbody>
                {reportsData.breakdown.byService.slice(0, 10).map((service, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-900">{service.serviceName}</div>
                    </td>
                    <td className="py-3 px-4 text-right font-medium">
                      {formatCurrency(service.totalSales)}
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-green-600">
                      {formatCurrency(service.totalProfit)}
                    </td>
                    <td className="py-3 px-4 text-right">{service.transactionCount}</td>
                    <td className="py-3 px-4 text-right">{formatCurrency(service.avgOrderValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Staff Performance (Admin Only) */}
      {userRole === 'Admin' && reportsData?.breakdown?.byStaff && reportsData.breakdown.byStaff.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            <FaUsers className="inline mr-2" />
            Staff Performance
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Staff Member</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Sales</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Profit</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Orders</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Avg Order</th>
                </tr>
              </thead>
              <tbody>
                {reportsData.breakdown.byStaff.map((staff, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-900">{staff.staffName}</div>
                      <div className="text-sm text-gray-500">{staff.staffEmail}</div>
                    </td>
                    <td className="py-3 px-4 text-right font-medium">
                      {formatCurrency(staff.totalSales)}
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-green-600">
                      {formatCurrency(staff.totalProfit)}
                    </td>
                    <td className="py-3 px-4 text-right">{staff.transactionCount}</td>
                    <td className="py-3 px-4 text-right">{formatCurrency(staff.avgOrderValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {!reportsData && !loading && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="text-center py-12">
            <FaChartBar className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Data Available</h3>
            <p className="text-gray-500 mb-4">
              No sales data found for the selected period. Try adjusting your filters.
            </p>
            <button
              onClick={() => handleDateRangeChange('thisMonth')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
            >
              View This Month
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesReports;
